import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ArrowRight, CheckCircle2, Hourglass, Handshake, Sparkles } from 'lucide-react';

/**
 * Catch-Up Missions — Parent "This week's catch-ups" tile (CUM-4).
 *
 * Decisions A & B from the locked CUM-4 plan:
 *   - Scoped to the current child (caller passes `childId`).
 *   - Shows `needs_help_last_7d` as a count only (no topic names — privacy).
 *   - Never shows `skipped`.
 *   - Empty state → tile renders nothing.
 */
export default function WeeklyCatchupsTile({ childId, childName }) {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!childId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/parent/children/${childId}/missions/summary`);
      setSummary(data);
    } catch (e) {
      console.warn('[WeeklyCatchupsTile] load failed:', e);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [childId]);

  useEffect(() => { load(); }, [load]);

  if (loading || !summary) return null;
  const completedCount = summary.completed_last_7d || 0;
  const inProgress = summary.in_progress || 0;
  const needsHelp = summary.needs_help_last_7d || 0;
  const topics = summary.topics_last_7d || [];

  // Empty state — render nothing (decision per plan)
  if (completedCount === 0 && inProgress === 0 && needsHelp === 0) {
    return null;
  }

  const namePart = childName ? `· ${childName}` : '';

  return (
    <section
      className="bg-white border rounded-xl p-5 shadow-sm"
      data-testid="weekly-catchups-tile"
    >
      <header className="flex items-center gap-2 mb-4">
        <Sparkles size={16} className="text-amber-500" />
        <h3 className="text-sm font-semibold text-slate-900">
          This week's catch-ups <span className="text-slate-500 font-normal">{namePart}</span>
        </h3>
      </header>

      <div className="space-y-3" data-testid="weekly-catchups-rows">
        {completedCount > 0 && (
          <div className="flex items-start gap-3" data-testid="weekly-catchups-completed">
            <CheckCircle2 size={18} className="text-emerald-500 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-slate-900">
                {completedCount} completed
              </div>
              {topics.length > 0 && (
                <div className="text-xs text-slate-500 mt-0.5 truncate" title={topics.join(' · ')}>
                  {topics.join(' · ')}
                </div>
              )}
            </div>
          </div>
        )}

        {inProgress > 0 && (
          <div className="flex items-start gap-3" data-testid="weekly-catchups-in-progress">
            <Hourglass size={18} className="text-amber-500 shrink-0 mt-0.5" />
            <div className="text-sm text-slate-900">
              {inProgress} in progress
            </div>
          </div>
        )}

        {needsHelp > 0 && (
          <div className="flex items-start gap-3" data-testid="weekly-catchups-needs-help">
            <Handshake size={18} className="text-indigo-500 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="text-sm text-slate-900">
                {needsHelp} topic{needsHelp === 1 ? '' : 's'} flagged for teacher follow-up
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5">
                Your child put in real work here — their teacher will close the gap in class.
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="mt-4 pt-3 border-t border-slate-100">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(`/parent/children/${childId}/missions`)}
          data-testid="weekly-catchups-cta"
        >
          See {childName ? `${childName}'s` : 'all'} missions
          <ArrowRight size={14} className="ml-1" />
        </Button>
      </div>
    </section>
  );
}
